import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadRegistry, type NodeTemplate } from '@features/flows/palette/registry';
import { useCanvasStore } from '@features/flows/state/canvas-store';
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from '@/components/ui/command';
import { cn } from '@shared/utils/cn';
import PaletteIcon from '@shared/ui/palette-icon';
import env from '@shared/api/env';

export function CommandBar(props: { onSelect: (tpl: NodeTemplate) => void }) {
  const { ui, closeCommandBar, setCommandBarQuery } = useCanvasStore();
  const open = ui.commandBar.open;
  const q = ui.commandBar.query;
  const refInput = useRef<HTMLInputElement>(null);

  // All templates
  const [items, setItems] = useState<NodeTemplate[]>([]);
  // Packages/groups fetched from server (for packages view)
  type PackageHandler = { name: string } & Partial<{ title: string; description: string; icon: string; category: string; version: string }>;
  type PackageGroup = { id: string; title: string; description?: string; icon?: string; handlerCount: number; handlers: PackageHandler[] };
  const [pkgGroups, setPkgGroups] = useState<PackageGroup[]>([]);
  const [keyToPkgId, setKeyToPkgId] = useState<Map<string, string>>(new Map());
  // Step selection state
  const [currentPkg, setCurrentPkg] = useState<string | null>(null);
  const [sel, setSel] = useState(0); // actions selection
  const [pkgSel, setPkgSel] = useState(0); // packages selection
  const [pkgTab, setPkgTab] = useState<'all' | 'starred' | 'recent'>('all');
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const registerItemRef = useCallback((key: string, el: HTMLButtonElement | null) => {
    if (el) itemRefs.current.set(key, el);
    else itemRefs.current.delete(key);
  }, []);
  // Prevent hover selection from fighting keyboard scrolling
  const hoverGuard = useRef<{ suppress: boolean; t?: any }>({ suppress: false });
  const suppressHoverBriefly = useCallback(() => {
    hoverGuard.current.suppress = true;
    if (hoverGuard.current.t) clearTimeout(hoverGuard.current.t);
    hoverGuard.current.t = setTimeout(() => { hoverGuard.current.suppress = false; }, 180);
  }, []);

  // Local memory (pins & usage)
  const PINNED_PKGS_KEY = 'palette.pinnedPkgs.v1';
  const PINNED_NODES_KEY = 'palette.pinnedNodes.v1';
  const USAGE_KEY = 'palette.usage.v1';
  const readJson = useCallback(<T,>(k: string, fallback: T): T => {
    try { const v = localStorage.getItem(k); return v ? (JSON.parse(v) as T) : fallback; } catch { return fallback; }
  }, []);
  const writeJson = useCallback(<T,>(k: string, v: T) => {
    try { localStorage.setItem(k, JSON.stringify(v)); } catch { }
  }, []);

  useEffect(() => {
    if (!open) return;
    let mounted = true;

    // Load node templates (actions)
    loadRegistry().then((map) => {
      if (!mounted) return;
      setItems(Array.from(map.values()));
      setSel(0);
      setPkgSel(0);
      // Default to global "All" actions view
      setCurrentPkg('__GLOBAL__');
      setTimeout(() => refInput.current?.focus(), 0);
    }).catch(() => {
      // ignore; errors surface later when opening actions pane
    });

    // Load package groups for packages view
    (async () => {
      const CACHE_KEY = 'palette.packages.cache.v1';
      const base = String((env as any)?.CONDUIT_BASE || (env as any)?.BLAST_API || '').replace(/\/$/, '');
      const url = base ? `${base}/packages?splitCoreBy=category` : '';
      try {
        if (!url) throw new Error('Missing CONDUIT_BASE/BLAST_API');
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const groups: PackageGroup[] = Array.isArray(json?.items) ? json.items : [];
        if (mounted) {
          setPkgGroups(groups);
          const map = new Map<string, string>();
          for (const g of groups) {
            for (const h of g.handlers || []) map.set(h.name, g.id);
          }
          setKeyToPkgId(map);
        }
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(json)); } catch { }
      } catch {
        // Fallback to cache if available
        try {
          const cached = localStorage.getItem('palette.packages.cache.v1');
          if (cached) {
            const parsed = JSON.parse(cached);
            const groups: PackageGroup[] = Array.isArray(parsed?.items) ? parsed.items : [];
            if (mounted) {
              setPkgGroups(groups);
              const map = new Map<string, string>();
              for (const g of groups) {
                for (const h of g.handlers || []) map.set(h.name, g.id);
              }
              setKeyToPkgId(map);
            }
          }
        } catch { }
      }
    })();

    return () => { mounted = false; };
  }, [open]);

  // Keep the search input focused when switching screens (packages ⇄ actions/global)
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => refInput.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [currentPkg, open]);

  // Usage map (per open)
  const usage = useMemo(() => readJson<Record<string, { count: number; last: number; pkg: string }>>(USAGE_KEY, {}), [open]);
  const bumpUsage = useCallback((tpl: NodeTemplate) => {
    const u = readJson<Record<string, { count: number; last: number; pkg: string }>>(USAGE_KEY, {});
    const now = Date.now();
    const prev = u[tpl.key] || { count: 0, last: 0, pkg: tpl.pkg };
    u[tpl.key] = { count: prev.count + 1, last: now, pkg: tpl.pkg };
    writeJson(USAGE_KEY, u);
  }, [readJson, writeJson]);

  // --- Packages screen data ---
  const pinnedPkgs = useMemo(() => new Set(readJson<string[]>(PINNED_PKGS_KEY, [])), [open]);
  type PkgCard = { name: string; title?: string; description?: string; count: number; icon?: string };

  // All package entries (flat), independent of tabs
  const pkgEntriesAll = useMemo<PkgCard[]>(() => {
    if (pkgGroups.length > 0) {
      return pkgGroups.map((g) => ({ name: g.id, title: g.title, description: g.description, count: g.handlerCount, icon: g.icon }));
    }
    // Fallback: derive from items when groups are unavailable
    const by = new Map<string, PkgCard>();
    for (const t of items) {
      const p = t.pkg || 'misc';
      const r = by.get(p) || { name: p, count: 0, icon: t.icon };
      r.count += 1;
      if (!r.icon && t.icon) r.icon = t.icon;
      by.set(p, r);
    }
    return Array.from(by.values());
  }, [pkgGroups, items]);

  // Scores for recency by package id
  const pkgRecentScore = useMemo(() => {
    const recentScore = new Map<string, number>();
    Object.entries(usage).forEach(([key, u]) => {
      const pkgId = keyToPkgId.get(key) || u.pkg;
      if (!pkgId) return;
      recentScore.set(pkgId, Math.max(recentScore.get(pkgId) ?? 0, u.last));
    });
    return recentScore;
  }, [usage, keyToPkgId]);

  // Legacy buckets retained for any dependent logic (unused in UI after tabs)
  const pkgs = useMemo(() => {
    const entries = pkgEntriesAll;
    const recentScore = pkgRecentScore;
    const starred = entries.filter((e) => pinnedPkgs.has(e.name));
    const recent = entries
      .filter((e) => !pinnedPkgs.has(e.name))
      .sort((a, b) => (recentScore.get(b.name) ?? 0) - (recentScore.get(a.name) ?? 0))
      .slice(0, 8);
    const rest = entries
      .filter((e) => !pinnedPkgs.has(e.name) && !recent.find((r) => r.name === e.name))
      .sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name));
    return { starred, recent, rest };
  }, [pkgEntriesAll, pinnedPkgs, pkgRecentScore]);

  const qPkg = q.trim().toLowerCase();
  const filteredPkgs = useMemo(() => {
    const match = (p: PkgCard) => !qPkg || (p.title || p.name).toLowerCase().includes(qPkg);
    const f = (arr: PkgCard[]) => arr.filter(match);
    return { starred: f(pkgs.starred), recent: f(pkgs.recent), rest: f(pkgs.rest) };
  }, [pkgs, qPkg]);
  // Determine visible packages based on current tab
  const visiblePkgs: PkgCard[] = useMemo(() => {
    const filterByQuery = (list: PkgCard[]) => list.filter((p) => {
      const name = (p.title || p.name).toLowerCase();
      return !qPkg || name.includes(qPkg);
    });
    if (pkgTab === 'starred') {
      const starred = pkgEntriesAll.filter((p) => pinnedPkgs.has(p.name));
      return filterByQuery(starred);
    }
    if (pkgTab === 'recent') {
      const recents = pkgEntriesAll
        .filter((p) => !pinnedPkgs.has(p.name))
        .sort((a, b) => (pkgRecentScore.get(b.name) ?? 0) - (pkgRecentScore.get(a.name) ?? 0));
      return filterByQuery(recents);
    }
    // all: alphabetical by title/name
    const all = pkgEntriesAll.slice().sort((a, b) => (a.title || a.name).localeCompare(b.title || b.name));
    return filterByQuery(all);
  }, [pkgTab, qPkg, pkgEntriesAll, pinnedPkgs, pkgRecentScore]);

  // Flatten for keyboard selection equals visible set
  const pkgFlat: PkgCard[] = visiblePkgs;

  // --- Actions screen data ---
  const pinnedNodes = useMemo(() => new Set(readJson<string[]>(PINNED_NODES_KEY, [])), [open]);
  const pkgItems = useMemo(() => {
    if (!currentPkg || currentPkg === '__GLOBAL__') return [];
    // Prefer server-defined package membership; fallback to legacy category-based filter
    const currentGroup = pkgGroups.find((g) => g.id === currentPkg);
    let base: NodeTemplate[];
    if (currentGroup) {
      const keys = new Set((currentGroup.handlers || []).map((h) => h.name));
      base = items.filter((t) => keys.has(t.key));
    } else {
      base = items.filter((t) => t.pkg === currentPkg);
    }
    const s = q.trim().toLowerCase();
    if (!s) return base;
    return base.filter((t) =>
      t.title.toLowerCase().includes(s) ||
      t.key.toLowerCase().includes(s) ||
      t.category.toLowerCase().includes(s) ||
      (t.synonyms || []).some((x) => x.toLowerCase().includes(s))
    );
  }, [items, currentPkg, q, pkgGroups]);

  const orderedPkgItems = useMemo(() => {
    if (!currentPkg || currentPkg === '__GLOBAL__') return [];
    const score = (t: NodeTemplate) => {
      const u = usage[t.key];
      const recency = u ? (Date.now() - u.last) : Number.POSITIVE_INFINITY; // lower is better
      const pinnedBoost = pinnedNodes.has(t.key) ? -1_000_000 : 0;
      const starterBoost = t.starter ? -20_000 : 0;
      const qBoost = q ? (t.title.toLowerCase().includes(q.toLowerCase()) ? -5_000 : 0) : 0;
      return recency + pinnedBoost + starterBoost + qBoost;
    };
    return pkgItems.slice().sort((a, b) => score(a) - score(b));
  }, [pkgItems, usage, pinnedNodes, currentPkg, q]);

  // Clamp selection indices
  useEffect(() => {
    setSel(s => Math.min(Math.max(0, s), Math.max(0, orderedPkgItems.length - 1)));
  }, [orderedPkgItems.length]);
  useEffect(() => {
    setPkgSel(s => Math.min(Math.max(0, s), Math.max(0, pkgFlat.length - 1)));
  }, [pkgFlat.length]);

  // Scroll selected action into view (package-scoped)
  useEffect(() => {
    if (!open || !currentPkg || currentPkg === '__GLOBAL__') return;
    const it = orderedPkgItems[sel];
    if (!it) return;
    const el = itemRefs.current.get(it.key);
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, sel, orderedPkgItems, currentPkg]);

  // Prepare actions/global list and keep selection/scroll effects above any early returns
  const inGlobal = currentPkg === '__GLOBAL__';
  const actions = useMemo(() => {
    if (inGlobal) {
      const s = q.trim().toLowerCase();
      if (!s) return items.slice(0, 500);
      return items.filter(t =>
        t.title.toLowerCase().includes(s) ||
        t.key.toLowerCase().includes(s) ||
        t.pkg.toLowerCase().includes(s)
      );
    }
    return orderedPkgItems;
  }, [inGlobal, q, items, orderedPkgItems]);

  // Clamp selection when actions list changes in global mode
  useEffect(() => {
    if (!inGlobal) return;
    setSel((s) => Math.min(Math.max(0, s), Math.max(0, actions.length - 1)));
  }, [actions.length, inGlobal]);

  // Scroll selected action into view (global mode)
  useEffect(() => {
    if (!open || !inGlobal) return;
    const it = actions[sel];
    if (!it) return;
    const el = itemRefs.current.get(it.key);
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, sel, inGlobal, actions]);

  if (!open) return null;

  // --- Packages screen ---
  if (!currentPkg) {
    const onOpenSelected = (idx: number) => {
      const target = pkgFlat[idx];
      if (target) setCurrentPkg(target.name);
    };
    return (
      <div
        className="fixed inset-0 z-[60]"
        onClick={closeCommandBar}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.stopPropagation(); closeCommandBar(); }
        }}
        aria-hidden="false"
      >
        <div
          className="absolute w-[860px] h-[640px]"
          style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <Command className="w-full h-full shadow-2xl">
            {/* Top-level tabs: All (global actions) · Packages · Starred · Recent */}
            <div className="px-3 pt-3">
              <div className="inline-flex gap-1 rounded-md border p-1 bg-background" role="tablist" aria-label="Mode tabs">
                {/* All (global) */}
                <button
                  role="tab"
                  aria-selected={false}
                  className={cn('px-2 py-1 text-xs rounded-sm border border-transparent text-muted-foreground hover:text-foreground')}
                  onClick={() => { setCurrentPkg('__GLOBAL__'); setSel(0); }}
                >
                  All
                </button>
                {/* Packages (all) */}
                <button
                  role="tab"
                  aria-selected={pkgTab === 'all'}
                  className={cn('px-2 py-1 text-xs rounded-sm border', pkgTab === 'all' ? 'bg-accent border-accent text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
                  onClick={() => { setCurrentPkg(null); setPkgTab('all'); setPkgSel(0); }}
                >
                  Packages
                </button>
                {/* Starred packages */}
                <button
                  role="tab"
                  aria-selected={pkgTab === 'starred'}
                  className={cn('px-2 py-1 text-xs rounded-sm border', pkgTab === 'starred' ? 'bg-accent border-accent text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
                  onClick={() => { setCurrentPkg(null); setPkgTab('starred'); setPkgSel(0); }}
                >
                  Starred
                </button>
                {/* Recent packages */}
                <button
                  role="tab"
                  aria-selected={pkgTab === 'recent'}
                  className={cn('px-2 py-1 text-xs rounded-sm border', pkgTab === 'recent' ? 'bg-accent border-accent text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
                  onClick={() => { setCurrentPkg(null); setPkgTab('recent'); setPkgSel(0); }}
                >
                  Recent
                </button>
              </div>
            </div>
            <CommandInput
              ref={refInput}
              placeholder="Search packages… (Enter opens)"
              value={q}
              onChange={(e) => setCommandBarQuery((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') closeCommandBar();
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') setPkgSel(v => Math.min(v + 1, Math.max(0, pkgFlat.length - 1)));
                if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') setPkgSel(v => Math.max(v - 1, 0));
                if (e.key === 'Enter') onOpenSelected(pkgSel);
                if ((e.key === 'Backspace' && !q) || (e.altKey && e.key === 'ArrowLeft')) closeCommandBar();
              }}
            />
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="px-3 py-2 text-xs text-muted-foreground">
                Type to filter packages. Enter opens. Press &gt; to search everywhere.
              </div>
              {/* Package grid (filtered by top-level tabs) */}
              {/* Visible grid for current tab */}
              <section className="px-3 py-2 min-h-0 flex-1 flex">
                <div className="grid grid-cols-3 gap-2 overflow-auto pr-1 w-full min-h-0">
                  {visiblePkgs.map((p) => {
                    const isSelected = pkgFlat[pkgSel]?.name === p.name;
                    return (
                      <button
                        key={`pkg:${p.name}`}
                        className={cn('rounded border p-3 text-left hover:bg-accent', isSelected && 'ring-2 ring-accent')}
                        onClick={() => setCurrentPkg(p.name)}
                      >
                        <div className="flex items-start gap-2 mb-1">
                          {p.icon && (
                            <span className="inline-flex items-center justify-center mt-0.5">
                              <PaletteIcon icon={p.icon} />
                            </span>
                          )}
                          <div className="font-medium min-w-0 truncate">{p.title || p.name}</div>
                        </div>
                        {p.description && (
                          <div className="text-xs text-muted-foreground truncate" title={p.description}>{p.description}</div>
                        )}
                        <div className="text-[11px] text-muted-foreground mt-1">{p.count} actions</div>
                      </button>
                    );
                  })}
                  {pkgFlat.length === 0 && (
                    <div className="text-sm text-muted-foreground">No packages match “{q}”.</div>
                  )}
                </div>
              </section>
            </div>
          </Command>
        </div>
      </div>
    );
  }

  // --- Actions screen (specific package or optional "__GLOBAL__") ---

  return (
    <div className="fixed inset-0 z-[60]" onClick={closeCommandBar}>
      <div className="absolute w-[860px] h-[640px]" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} onClick={(e) => e.stopPropagation()}>
        <Command
          className="w-full h-full shadow-2xl"
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.stopPropagation(); closeCommandBar(); }
            if ((e.key === 'Backspace' && !q) || (e.altKey && e.key === 'ArrowLeft')) {
              setCurrentPkg(null); setSel(0); return;
            }
          }}
        >
          <div className="flex items-center justify-between px-3 pt-3">
            <div className="text-xs text-muted-foreground">
              {/* Top-level mode tabs: All (global actions) · Packages · Starred · Recent */}
              <div className="inline-flex gap-1 rounded-md border p-1 bg-background" role="tablist" aria-label="Mode tabs">
                {/* All (global) */}
                <button
                  role="tab"
                  aria-selected={inGlobal}
                  className={cn('px-2 py-1 text-xs rounded-sm border', inGlobal ? 'bg-accent border-accent text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
                  onClick={() => { setCurrentPkg('__GLOBAL__'); setSel(0); }}
                >
                  All
                </button>
                {/* Packages (all) */}
                <button
                  role="tab"
                  aria-selected={!inGlobal}
                  className={cn('px-2 py-1 text-xs rounded-sm border', !inGlobal ? 'bg-accent border-accent text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
                  onClick={() => { setCurrentPkg(null); setPkgTab('all'); setPkgSel(0); }}
                >
                  Packages
                </button>
                {/* Starred packages */}
                <button
                  role="tab"
                  aria-selected={false}
                  className={cn('px-2 py-1 text-xs rounded-sm border border-transparent text-muted-foreground hover:text-foreground')}
                  onClick={() => { setCurrentPkg(null); setPkgTab('starred'); setPkgSel(0); }}
                >
                  Starred
                </button>
                {/* Recent packages */}
                <button
                  role="tab"
                  aria-selected={false}
                  className={cn('px-2 py-1 text-xs rounded-sm border border-transparent text-muted-foreground hover:text-foreground')}
                  onClick={() => { setCurrentPkg(null); setPkgTab('recent'); setPkgSel(0); }}
                >
                  Recent
                </button>
              </div>
            </div>
            {!inGlobal && (
              <button
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => {
                  const pinned = new Set(readJson<string[]>(PINNED_PKGS_KEY, []));
                  if (pinned.has(currentPkg!)) pinned.delete(currentPkg!); else pinned.add(currentPkg!);
                  writeJson(PINNED_PKGS_KEY, Array.from(pinned));
                }}
              >
                ☆ Pin package
              </button>
            )}
          </div>
          <CommandInput
            ref={refInput}
            placeholder={inGlobal ? 'Search all actions…' : `Search ${(pkgGroups.find(g => g.id === currentPkg)?.title || currentPkg)} actions…`}
            value={q}
            onChange={(e) => setCommandBarQuery((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { suppressHoverBriefly(); setSel((v) => Math.min(v + 1, Math.max(0, actions.length - 1))); }
              if (e.key === 'ArrowUp') { suppressHoverBriefly(); setSel((v) => Math.max(v - 1, 0)); }
              if (e.key === 'Enter' && actions[sel]) {
                const tpl = actions[sel];
                bumpUsage(tpl);
                props.onSelect(tpl);
                try { (useCanvasStore as any).getState().closeCommandBar(); } catch { closeCommandBar(); }
              }
            }}
          />
          <div className="grid grid-cols-2 gap-2 min-h-0 flex-1">
            <div className="min-h-0 h-full" onMouseMove={() => { hoverGuard.current.suppress = false; }}>
              <div className="text-xs text-muted-foreground px-3 py-1">
                Showing {actions.length} {actions.length === 1 ? 'action' : 'actions'}
              </div>
              <CommandList className="h-full max-h-none overflow-auto">
                {actions.length === 0 && <CommandEmpty>No results</CommandEmpty>}
                <CommandGroup>
                  {actions.map((it, i) => (
                    <Row
                      key={it.key}
                      item={it}
                      index={i}
                      selected={i === sel}
                      onHoverIndex={(idx) => { if (!hoverGuard.current.suppress) setSel(idx); }}
                      onClickItem={(tpl) => {
                        bumpUsage(tpl);
                        props.onSelect(tpl);
                        try { (useCanvasStore as any).getState().closeCommandBar(); } catch { closeCommandBar(); }
                      }}
                      registerRef={registerItemRef}
                    />
                  ))}
                </CommandGroup>
              </CommandList>
            </div>

            {/* Right pane: details for the hovered/selected item */}
            <div className="border-l bg-background/50 p-3 text-xs overflow-auto min-h-0 h-full">
              {(() => {
                const selItem = actions[sel];
                if (!selItem) {
                  return (
                    <div className="text-muted-foreground">
                      Tip: Type to search {inGlobal ? 'globally' : `within ${currentPkg}`}. Backspace goes back to packages when empty.
                    </div>
                  );
                }
                return (
                  <div className="space-y-2">
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                        {selItem.icon && (
                          <span className="inline-flex items-center justify-center">
                            <PaletteIcon icon={selItem.icon} />
                          </span>
                        )}
                        <span>{selItem.title}</span>
                        <span className="text-muted-foreground">· {selItem.category}</span>
                        {(() => {
                          const u = usage[selItem.key];
                          const isPinned = pinnedNodes.has(selItem.key);
                          const fmtAgo = (ts?: number) => {
                            if (!ts) return '';
                            const d = Date.now() - ts;
                            const s = Math.max(1, Math.floor(d / 1000));
                            if (s < 60) return `${s}s ago`;
                            const m = Math.floor(s / 60);
                            if (m < 60) return `${m}m ago`;
                            const h = Math.floor(m / 60);
                            if (h < 24) return `${h}h ago`;
                            const days = Math.floor(h / 24);
                            return `${days}d ago`;
                          };
                          return (
                            <>
                              {isPinned && (
                                <span className="text-[10px] uppercase tracking-wide border rounded px-1 py-0.5 text-muted-foreground">Pinned</span>
                              )}
                              {u && (
                                <span className="text-[10px] text-muted-foreground">
                                  Used {u.count}× · {fmtAgo(u.last)}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      {selItem.description && (
                        <div className="text-muted-foreground mt-1">{selItem.description}</div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="uppercase tracking-wide text-[10px] text-muted-foreground mb-1">Exec Inputs</div>
                        <div className="flex flex-wrap gap-1">
                          {(selItem.ports?.exec?.in ?? []).map((p) => (
                            <span key={`x-in-${p.id}`} className="rounded border bg-muted/30 px-1.5 py-0.5 font-mono">{p.id}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wide text-[10px] text-muted-foreground mb-1">Exec Outputs</div>
                        <div className="flex flex-wrap gap-1">
                          {(selItem.ports?.exec?.out ?? []).map((p) => (
                            <span key={`x-out-${p.id}`} className="rounded border bg-muted/30 px-1.5 py-0.5 font-mono">{p.id}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wide text-[10px] text-muted-foreground mb-1">Inputs</div>
                      <div className="flex flex-wrap gap-1">
                        {(selItem.ports?.data?.in ?? []).length === 0 && (
                          <span className="text-muted-foreground">None</span>
                        )}
                        {(selItem.ports?.data?.in ?? []).map((p) => (
                          <span key={p.id} className="rounded border bg-muted/30 px-1.5 py-0.5 font-mono">
                            {p.id}: {p.type}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wide text-[10px] text-muted-foreground mb-1">Outputs</div>
                      <div className="flex flex-wrap gap-1">
                        {(selItem.ports?.data?.out ?? []).length === 0 && (
                          <span className="text-muted-foreground">None</span>
                        )}
                        {(selItem.ports?.data?.out ?? []).map((p) => (
                          <span key={p.id} className="rounded border bg-muted/30 px-1.5 py-0.5 font-mono">
                            {p.id}: {p.type}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="uppercase tracking-wide text-[10px] text-muted-foreground mb-1">Attributes</div>
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          const schema: any = (selItem as any).attrsSchema || {};
                          const props = schema.properties || {};
                          const keys = Object.keys(props);
                          if (keys.length === 0) return <span className="text-muted-foreground">None</span>;
                          return keys.map((k) => {
                            const s: any = props[k] || {};
                            const t = s.type ?? 'any';
                            const d = typeof s.default !== 'undefined' ? ` = ${JSON.stringify(s.default)}` : '';
                            const en = Array.isArray(s.enum) && s.enum.length > 0 ? ` · enum: ${s.enum.join(' | ')}` : '';
                            return (
                              <span key={k} className="rounded border bg-muted/30 px-1.5 py-0.5 font-mono">
                                {k}: {String(t)}{d}{en}
                              </span>
                            );
                          });
                        })()}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="uppercase tracking-wide text-[10px] text-muted-foreground mb-1">Input Schema</div>
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            const schema: any = (selItem as any).inputSchema || {};
                            const props = schema.properties || {};
                            const req = new Set<string>(Array.isArray(schema.required) ? schema.required : []);
                            const keys = Object.keys(props);
                            if (keys.length === 0) return <span className="text-muted-foreground">None</span>;
                            return keys.map((k) => {
                              const s: any = props[k] || {};
                              const t = s.type ?? 'any';
                              const mark = req.has(k) ? '*' : '';
                              const dv = typeof s.default !== 'undefined' ? ` = ${JSON.stringify(s.default)}` : '';
                              return (
                                <span key={`in-${k}`} className="rounded border bg-muted/30 px-1.5 py-0.5 font-mono">
                                  {k}{mark}: {String(t)}{dv}
                                </span>
                              );
                            });
                          })()}
                        </div>
                      </div>
                      <div>
                        <div className="uppercase tracking-wide text-[10px] text-muted-foreground mb-1">Output Schema</div>
                        <div className="flex flex-wrap gap-1">
                          {(() => {
                            const schema: any = (selItem as any).outputSchema || {};
                            const props = schema.properties || {};
                            const req = new Set<string>(Array.isArray(schema.required) ? schema.required : []);
                            const keys = Object.keys(props);
                            if (keys.length === 0) return <span className="text-muted-foreground">None</span>;
                            return keys.map((k) => {
                              const s: any = props[k] || {};
                              const t = s.type ?? 'any';
                              const mark = req.has(k) ? '*' : '';
                              return (
                                <span key={`out-${k}`} className="rounded border bg-muted/30 px-1.5 py-0.5 font-mono">
                                  {k}{mark}: {String(t)}
                                </span>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </Command>
      </div>
    </div>
  );
}

export default CommandBar;

type RowProps = {
  item: NodeTemplate;
  index: number;
  selected: boolean;
  onHoverIndex: (index: number) => void;
  onClickItem: (item: NodeTemplate) => void;
  registerRef: (key: string, el: HTMLButtonElement | null) => void;
};

const Row = React.memo(function Row({ item, index, selected, onHoverIndex, onClickItem, registerRef }: RowProps) {
  return (
    <CommandItem
      selected={selected}
      onMouseEnter={() => onHoverIndex(index)}
      onClick={() => onClickItem(item)}
      ref={(el) => registerRef(item.key, el)}
    >
      <div className="flex items-start gap-2 min-w-0">
        {item.icon && (
          <span className="mt-0.5 inline-flex items-center justify-center">
            <PaletteIcon icon={item.icon} />
          </span>
        )}
        <div className="min-w-0">
          <div className="font-medium truncate">{item.title}</div>
          <div className="text-xs text-muted-foreground truncate">{item.description || `${item.category} — ${item.key}`}</div>
        </div>
      </div>
    </CommandItem>
  );
});
